package com.essntl.features.itinerary.data.supabase.itinerary_service

import com.essntl.features.address.data.supabase.AddressDto
import com.essntl.features.contact.data.supabase.ContactDto
import com.essntl.features.currency.data.supabase.CurrencyDto
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.price.data.supabase.staticprice.StaticPriceDto
import com.essntl.features.service.data.supabase.service.ServiceDto
import com.essntl.features.supplier.data.supabase.SupplierDto
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class ItineraryServiceDto(
    val id: String,
    @SerialName("service_id_data")
    val service: ServiceDto,
    val grouped: Boolean,
    @SerialName("additional_info")
    val additionalInfo: String?,
    @SerialName("edited_description")
    val editedDescription: String?,
    @SerialName("start_date")
    val startDate: String,
    @SerialName("end_date")
    val endDate: String?,
    @SerialName("start_time")
    val startTime: String?,
    @SerialName("end_time")
    val endTime: String?,
    val status: String?,
    @SerialName("created_at")
    val createdAt: String,
    @SerialName("updated_at")
    val updatedAt: String?,
    val price: Int?,
    val cost: Int?,
    @SerialName("internal_notes")
    val internalNotes: String?,
    @SerialName("supplier_id_data")
    val supplier: SupplierDto?,
    @SerialName("guest_count")
    val guestCount: Int,
    @SerialName("address_id_data")
    val address: AddressDto?,
    @SerialName("address")
    val meetingPoint: String?,
    @SerialName("updated_by")
    val updatedBy: String?,
    @SerialName("itinerary_id")
    val itineraryId: String?,
    @SerialName("dynamic_fields")
    val dynamicFields: List<DynamicFieldDto>?,
    @SerialName("special_notes")
    val specialNotes: String?,
    val paid: Boolean?,
    @SerialName("embedded_map")
    val embeddedMap: String?,
    @SerialName("embedded_content")
    val embeddedContent: String?,
    val files: List<String>?,
    @SerialName("linked_id")
    val linkedId: String? = null,
    @SerialName("po_sent_at")
    val poSentAt: String? = null,
    @SerialName("booked_by_ta")
    val bookedByTa: Boolean?,
    @SerialName("price_currency_data")
    val priceCurrency: CurrencyDto?,
    @SerialName("cost_currency_data")
    val costCurrency: CurrencyDto?,
    @SerialName("supplier_contact_id_data")
    val supplierContact: ContactDto? = null,
    @SerialName("static_price_id_data")
    val staticPrice: StaticPriceDto?,
) {
    fun toModel() =
        ItineraryServiceModel(
            id = id,
            service = service.toModel(),
            grouped = grouped,
            additionalInfo = additionalInfo,
            editedDescription = editedDescription,
            startDate = startDate,
            endDate = endDate,
            startTime = startTime,
            endTime = endTime,
            createdAt = createdAt,
            updatedAt = updatedAt,
            price = price,
            cost = cost,
            supplier = supplier?.toModel(),
            staticPrice = staticPrice?.toModel(),
            supplierContact = supplierContact?.toModel(),
            internalNotes = internalNotes,
            guestCount = guestCount,
            address = address?.toModel(),
            updatedBy = updatedBy,
            itineraryId = itineraryId,
            dynamicFields = dynamicFields?.map { it.toModel() },
            specialNotes = specialNotes,
            paid = paid,
            embeddedMap = embeddedMap,
            embeddedContent = embeddedContent,
            files = files?.toSet(),
            linkedId = linkedId,
            poSentAt = poSentAt,
            bookedByTa = bookedByTa,
            priceCurrency = priceCurrency?.toModel(),
            costCurrency = costCurrency?.toModel(),
            meetingPoint = meetingPoint,
            status =
                when (status) {
                    "READY" -> ItineraryServiceModel.Status.Ready
                    "PO_SENT" -> ItineraryServiceModel.Status.POSent
                    "CONFIRMED" -> ItineraryServiceModel.Status.Confirmed
                    "TO_CHANGE" -> ItineraryServiceModel.Status.TOChange
                    "CANCELLED" -> ItineraryServiceModel.Status.Cancelled
                    "COMPLETED" -> ItineraryServiceModel.Status.Completed
                    else -> null
                },
        )

    companion object {
        fun getColumns() = Columns.raw(COLUMNS_VALUE)

        const val COLUMNS_VALUE =
            "*, supplier_id_data:supplier_id(${SupplierDto.COLUMNS_VALUE}), " +
                "service_id_data:service_id(${ServiceDto.COLUMNS_VALUE}), " +
                "supplier_contact_id_data:supplier_contact_id(${ContactDto.COLUMNS_VALUE}), " +
                "address_id_data:address_id(*)" +
                ", static_price_id_data:static_price_id(${StaticPriceDto.COLUMNS_VALUE})" +
                ", price_currency_data:price_currency(*)" +
                ", cost_currency_data:cost_currency(*)"
    }
}

internal fun ItineraryServiceModel.Status.toDto() =
    when (this) {
        ItineraryServiceModel.Status.Ready -> "READY"
        ItineraryServiceModel.Status.POSent -> "PO_SENT"
        ItineraryServiceModel.Status.Confirmed -> "CONFIRMED"
        ItineraryServiceModel.Status.TOChange -> "TO_CHANGE"
        ItineraryServiceModel.Status.Cancelled -> "CANCELLED"
        ItineraryServiceModel.Status.Completed -> "COMPLETED"
    }